import React from "react";
import {AutoComplete, Button, Form, Input, Modal, Space, Tag, Tooltip, Typography} from "antd";
import {AI_MODEL_OPTIONS, filterAiModelOption} from "@shared/aiModels.js";
import {getTypeBadge} from "./typeBadge.js";

const {Text} = Typography;

const NEXT = {
	ROOT: "CONSORZIO",
	CONSORZIO: "COOPERATIVA",
	COOPERATIVA: "CANALE",
	CANALE: null
};

export default function OrgNodeDetails({selected, path, onAdd, onRename, onDelete, isRoot, canAddChild, type, aiModel, globalAiModel, onSaveAiModel})
{
	const b = getTypeBadge(type);

	const [openAdd, setOpenAdd] = React.useState(false);
	const [openRen, setOpenRen] = React.useState(false);
	const [openAi, setOpenAi] = React.useState(false);
	const [savingAi, setSavingAi] = React.useState(false);

	const [formAdd] = Form.useForm();
	const [formRen] = Form.useForm();
	const [formAi] = Form.useForm();

	const addRef = React.useRef(null);
	const renRef = React.useRef(null);

	const currentName = React.useMemo(() =>
	{
		if (!path?.length) return "";
		return path[path.length - 1].name || "";
	}, [path]);

	const nextType = React.useMemo(() =>
	{
		return NEXT[type] || null;
	}, [type]);

	// Il modello AI personalizzato esiste solo a livello COOPERATIVA (come i prompt custom):
	// vuoto = la cooperativa eredita il modello globale delle impostazioni.
	const isCooperativa = type === "COOPERATIVA";
	const currentAiModel = typeof aiModel === "string" ? aiModel.trim() : "";
	const inheritedLabel = globalAiModel ? `Eredita dal globale (${globalAiModel})` : "Eredita dal globale";

	const openAiModal = () =>
	{
		setOpenAi(true);
	};

	const submitAiModel = async (forcedValue) =>
	{
		let value = forcedValue;

		if (typeof value !== "string")
		{
			const v = await formAi.validateFields();
			value = v?.model || "";
		}

		setSavingAi(true);
		try
		{
			const ok = await onSaveAiModel?.(value.trim());
			if (ok !== false)
				setOpenAi(false);
		}
		finally
		{
			setSavingAi(false);
		}
	};

	const openAddModal = () =>
	{
		setOpenAdd(true);
	};

	const openRenameModal = () =>
	{
		setOpenRen(true);
	};

	const submitAdd = async () =>
	{
		const v = await formAdd.validateFields();
		if (!v?.name?.trim()) return;

		onAdd(v.name.trim());
		formAdd.resetFields();
		setOpenAdd(false);
	};

	const submitRen = async () =>
	{
		const v = await formRen.validateFields();
		if (!v?.name?.trim()) return;

		onRename(v.name.trim());
		setOpenRen(false);
	};

	if (!selected)
		return <Text type="secondary">Seleziona un nodo per vedere le azioni.</Text>;

	return (
		<>
			<div className="path">
				{path.map((n, i) =>
					<span key={n.key} className="crumb">
						{i > 0 ? " / " : ""}{n.name}
					</span>
				)}
				<Tag color={b.color} style={{...b.style, marginLeft: 10}}>
					{b.label}
				</Tag>
				{isCooperativa &&
					<Tooltip title={currentAiModel
						? "Modello AI personalizzato per questa cooperativa"
						: `Nessun modello personalizzato: usa quello globale${globalAiModel ? ` (${globalAiModel})` : ""}`}
					>
						<Tag color={currentAiModel ? "purple" : "default"} style={{marginLeft: 6}}>
							{currentAiModel ? `AI: ${currentAiModel}` : "AI: ereditato"}
						</Tag>
					</Tooltip>
				}
			</div>

			<div style={{marginTop: 12}}>
				<Space wrap>
					<Button type="primary" disabled={!canAddChild} onClick={openAddModal}>
						Aggiungi figlio
					</Button>
					<Button disabled={isRoot} onClick={openRenameModal}>
						Rinomina
					</Button>
					{isCooperativa &&
						<Button onClick={openAiModal}>
							Modello AI
						</Button>
					}
					<Button danger onClick={onDelete} disabled={isRoot}>
						Elimina
					</Button>
				</Space>
			</div>

			{/* Modal Aggiungi */}
			<Modal
				title={nextType ? `Nuovo ${nextType.toLowerCase()}` : "Nuovo elemento"}
				open={openAdd}
				onOk={submitAdd}
				onCancel={() => setOpenAdd(false)}
				okText="Crea"
				cancelText="Annulla"
				destroyOnHidden
				afterOpenChange={(open) =>
				{
					if (open)
						setTimeout(() => addRef.current?.focus(), 0);
				}}
			>
				<Form
					form={formAdd}
					layout="vertical"
					onFinish={submitAdd}
					initialValues={{name: ""}}
				>
					<Form.Item
						label="Nome"
						name="name"
						rules={[{required: true, message: "Inserisci un nome"}]}
					>
						<Input
							ref={addRef}
							size="large"
							placeholder={nextType ? `Nome ${nextType.toLowerCase()}…` : "Nome…"}
							onPressEnter={submitAdd}
							allowClear
						/>
					</Form.Item>

					{nextType &&
						<Text type="secondary">
							Verrà creato un <b>{nextType.toLowerCase()}</b> come figlio del nodo selezionato.
						</Text>
					}
				</Form>
			</Modal>

			{/* Modal Rinomina */}
			<Modal
				title="Rinomina"
				open={openRen}
				onOk={submitRen}
				onCancel={() => setOpenRen(false)}
				okText="Salva"
				destroyOnHidden
				cancelText="Annulla"
				afterOpenChange={(open) =>
				{
					if (open)
					{
						formRen.setFieldsValue({name: currentName});
						setTimeout(() => renRef.current?.focus(), 0);
					}
				}}
			>
				<Form
					form={formRen}
					layout="vertical"
					onFinish={submitRen}
					initialValues={{name: currentName}}
				>
					<Form.Item
						label="Nome"
						name="name"
						rules={[{required: true, message: "Inserisci un nome"}]}
					>
						<Input
							ref={renRef}
							size="large"
							placeholder="Nuovo nome…"
							onPressEnter={submitRen}
							allowClear
						/>
					</Form.Item>
				</Form>
			</Modal>

			{/* Modal Modello AI (solo COOPERATIVA) */}
			<Modal
				title="Modello AI"
				open={openAi}
				onCancel={() => setOpenAi(false)}
				destroyOnHidden
				footer={[
					<Button
						key="reset"
						onClick={() => submitAiModel("")}
						disabled={savingAi || !currentAiModel}
					>
						Ripristina globale
					</Button>,
					<Button key="cancel" onClick={() => setOpenAi(false)} disabled={savingAi}>
						Annulla
					</Button>,
					<Button key="save" type="primary" loading={savingAi} onClick={() => submitAiModel()}>
						Salva
					</Button>
				]}
				afterOpenChange={(open) =>
				{
					if (open)
						formAi.setFieldsValue({model: currentAiModel});
				}}
			>
				<Form
					form={formAi}
					layout="vertical"
					onFinish={() => submitAiModel()}
					initialValues={{model: currentAiModel}}
				>
					<Form.Item
						label="Modello utilizzato per l'estrazione AI"
						name="model"
						extra="Lascia vuoto per ereditare il modello impostato nelle impostazioni globali. Puoi digitare un modello non presente in elenco."
						rules={[{max: 100, message: "Massimo 100 caratteri"}]}
					>
						<AutoComplete
							options={AI_MODEL_OPTIONS}
							filterOption={filterAiModelOption}
							placeholder={inheritedLabel}
							allowClear
						/>
					</Form.Item>

					<Text type="secondary">
						Vale per ogni elaborazione dei volantini di <b>{currentName}</b>: estrazione, rielaborazione e varianti.
					</Text>
				</Form>
			</Modal>
		</>
	);
}
